import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { VENDOR_CURRENCIES, VendorCurrency } from '@classes/vendor-cost';
import { SettingsService } from '@services/settings.service';

const SYMBOLS: Record<string, string> = {
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CHF: 'CHF',
  USD: '$',
};

@Component({
  selector: 'app-vendor-margin-test',
  templateUrl: './vendor-margin-test.component.html',
  styleUrls: ['./vendor-margin-test.component.css'],
})
export class VendorMarginTestComponent implements OnInit, OnChanges {
  @Input() vendorName = '';
  @Input() vendorCurrency = 'EUR';

  currencies = [...VENDOR_CURRENCIES];
  currency = 'EUR';
  foreignCost = 120;
  exchangeRate = 1.125;
  shipping = 10.93;
  duty = 8.11;
  multiple = 3;
  private currencyTouched = false;

  constructor(private settings: SettingsService) {}

  ngOnInit(): void {
    this.settings.loadSettings().subscribe(() => this.applyRate());
  }

  ngOnChanges(): void {
    if (this.currencyTouched || !this.isCurrency(this.vendorCurrency)) {
      return;
    }
    this.currency = this.vendorCurrency;
    this.applyRate();
  }

  get symbol(): string {
    return this.symbolFor(this.currency);
  }

  get sliderMultiple(): number {
    return Math.min(10, this.num(this.multiple));
  }

  get sliderMargin(): number {
    return Math.min(90, this.marginPercent);
  }

  symbolFor(code: string): string {
    return SYMBOLS[code] || code;
  }

  num(value: number | string): number {
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  get convertedCost(): number {
    return this.num(this.foreignCost) * this.num(this.exchangeRate);
  }

  get convertedShipping(): number {
    return this.num(this.shipping) * this.num(this.exchangeRate);
  }

  get convertedDuty(): number {
    return this.num(this.duty) * this.num(this.exchangeRate);
  }

  get landed(): number {
    return this.convertedCost + this.convertedShipping + this.convertedDuty;
  }

  get proposedRetail(): number {
    return this.landed * this.num(this.multiple);
  }

  get grossProfit(): number {
    return this.proposedRetail - this.landed;
  }

  get marginPercent(): number {
    return this.proposedRetail ? (100 * this.grossProfit) / this.proposedRetail : 0;
  }

  get multipleFill(): string {
    return `${Math.min(100, (this.num(this.multiple) / 10) * 100)}%`;
  }

  get marginFill(): string {
    return `${Math.min(100, (this.marginPercent / 90) * 100)}%`;
  }

  get rateLabel(): string {
    const rate = this.num(this.exchangeRate);
    return rate >= 1 ? rate.toFixed(3) : rate.toFixed(4);
  }

  get signal(): 'red' | 'yellow' | 'green' {
    if (this.marginPercent < 60) {
      return 'red';
    }
    if (this.marginPercent < 66.7) {
      return 'yellow';
    }
    return 'green';
  }

  get signalLabel(): string {
    if (this.signal === 'red') {
      return 'Below minimum margin floor';
    }
    if (this.signal === 'yellow') {
      return 'Above floor · below target';
    }
    return 'Target margin met';
  }

  onCurrencyChange(currency: string): void {
    this.currencyTouched = true;
    this.currency = currency;
    this.applyRate();
  }

  setMultiple(value: string | number): void {
    this.multiple = this.clamp(this.num(value), 0, 10);
  }

  setMargin(value: string | number): void {
    const margin = this.clamp(this.num(value), 0, 90);
    this.multiple = margin >= 100 ? 0 : this.round(1 / (1 - margin / 100));
  }

  setRetail(value: string | number): void {
    const retail = Math.max(0, this.num(value));
    this.multiple = this.landed ? this.round(retail / this.landed) : 0;
  }

  review(): void {
    document.getElementById('vendor-margin-results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private applyRate(): void {
    const rate = this.settings.exchangeRate(this.currency);
    if (rate > 0) {
      this.exchangeRate = rate;
    }
  }

  private isCurrency(value: string): value is VendorCurrency {
    return this.currencies.includes(value as VendorCurrency);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private round(value: number): number {
    return Math.round(value * 10000) / 10000;
  }
}
